<script setup>
// The empty state of a Polaris AI page (phase 4.2, D-G5): the kit's Empty (title, a sentence, the action) over the
// kit's own cards, faded, as a picture of what the page will hold. The cards are decoration: hidden from assistive
// technology and out of the tab order (`inert`), so the keyboard never lands on a fake button. Without `backdrop`
// it is the Empty alone (a filtered list with no match, a queue with nothing to review).
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import Policy from 'dashboard/components/policy.vue';

defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  actionLabel: { type: String, default: '' },
  actionPerms: { type: Array, default: () => [] },
});
const emit = defineEmits(['click']);
</script>

<template>
  <section
    class="relative isolate w-full overflow-hidden"
    :class="$slots.backdrop ? 'min-h-[28rem]' : ''"
    data-polaris-empty
  >
    <div
      v-if="$slots.backdrop"
      class="pointer-events-none flex select-none flex-col gap-4 p-px opacity-50"
      aria-hidden="true"
      inert
    >
      <slot name="backdrop" />
    </div>
    <div
      :class="
        $slots.backdrop
          ? 'absolute inset-0 flex flex-col items-center justify-end bg-gradient-to-t from-n-surface-1 from-40% to-transparent'
          : 'flex flex-col items-center'
      "
    >
      <Empty class="flex-none">
        <EmptyHeader>
          <EmptyTitle>{{ title }}</EmptyTitle>
          <EmptyDescription v-if="description">
            {{ description }}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent v-if="actionLabel || $slots.default">
          <Policy v-if="actionLabel" :permissions="actionPerms">
            <Button size="sm" @click="emit('click')">{{ actionLabel }}</Button>
          </Policy>
          <slot />
        </EmptyContent>
      </Empty>
    </div>
  </section>
</template>
